import { ChangeDetectionStrategy, ChangeDetectorRef, Component, NgZone, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { Router } from '@angular/router';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatDialog } from '@angular/material/dialog';
import { MatOptionModule } from '@angular/material/core';

import { ConfirmModalComponent } from '../../../shared/components/confirm-modal/confirm-modal.component';

import { UserService } from '../../../shared/services/apis/platform/user.service';
import { UserSimpleStruct } from '../../../shared/services/structs/user-list.response';
import { UserRegisterComponent } from '../../../shared/components/user-register/user-register.component';

import { MenuComponent } from '../../../shared/components/menu/menu.component';
import { FooterComponent } from '../../../shared/components/footer/footer.component';
import { LoginValidationComponent } from '../../../book-create/login-validation.component/login-validation.component';
import { CategoryStruct } from '../../../shared/services/structs/category.struct';
import { AlertService, AlertType } from '../../../shared/services/alert.service';
import { CategoryService } from '../../../shared/services/apis/platform/category.service';
import { BookPrintService } from '../../../shared/services/apis/platform/book-print.service';
import { KitStruct, VideoChapterStruct } from '../../../shared/services/structs/book-publish-edit.struct';

import { FileUploadService } from '../../../shared/services/apis/platform/file-upload.service';
import { AdminBookPublicationService } from '../../../shared/services/apis/platform/admin-book-publication.service';
import { PublicSimulatorService } from '../../../shared/services/apis/platform/public-simulator.service';
import { BookCostCalc, getMinPrice, toMoney } from '../../../my-books/book-publish-edit.component/book-cost.calculator';
import { AdminBookPublicationResponse } from '../../../shared/services/responses/platform/admin-book-publication.response';
import { AdminBookPublicationRequest } from '../../../shared/services/requests/platform/admin-book-publication.request';
import { FileUploadCreateResponse, FileUploadFinalizeResponse } from '../../../shared/services/responses/platform/file-upload-create.response';
import { UploadPurposeEnum } from '../../../shared/services/enums/upload-purpose.enum';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';

type CategoryOptionItem = {
  isHeader: boolean;
  label: string;
  idCategory?: number;
  classKey?: number;
};

@Component({
  selector: 'app-admin-book-publication',
  standalone: true,
  templateUrl: './admin-book-publication.component.html',
  styleUrls: ['./admin-book-publication.component.css'],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatOptionModule,
    MatSelectModule,

    MenuComponent,
    FooterComponent,
    LoginValidationComponent,
    MatAutocompleteModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatExpansionModule,
    MatSlideToggleModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdminBookPublicationComponent implements OnInit {
  isLoading = false;

  /** Libera o livro para venda na Amazon assim que estiver sincronizado com o Base ERP. */
  isMarketplaceEnabled = false;

  public model!: FormGroup;

  // categorias
  public listCategories: CategoryStruct[] = [];
  private allOptions: CategoryOptionItem[] = [];
  public primaryOptions: CategoryOptionItem[] = [];
  public secondaryOptions: CategoryOptionItem[] = [];

  // prints
  public listBookPrint: any[] = [];

  // previews + labels
  authorPhotoPreview: string | null = null;
  frontCoverPreview: string | null = null;
  backCoverPreview: string | null = null;

  coverForPrintLabel: string | null = null;
  bookForPrintLabel: string | null = null;
  bookForEbookLabel: string | null = null;

  allUsers: UserSimpleStruct[] = [];
  filteredUsers: UserSimpleStruct[] = [];
  userSearch = '';
  selectedUser: UserSimpleStruct | null = null;
  lockUserSelect = false;
  trackByUser = (_: number, u: UserSimpleStruct) => u?.idUser ?? _;

  isPublishing = false;

  uploadLoading: Record<string, boolean> = {
    authorPhoto: false,
    frontCover: false,
    backCover: false,
    coverForPrint: false,
    bookForPrint: false,
    bookForEbook: false,
  };

  public listVideoChapter: VideoChapterStruct[] = [];

  private static readonly MAX_IMAGE_BYTES = 5 * 1024 * 1024;
  private static readonly MAX_PDF_BYTES = 210 * 1024 * 1024;

  private loggedIn = false;
  publicConfig: { platformProfitPercent: number; baseTaxPercent: number; productionCostProfitMarginPercent: number } | null = null;

  constructor(
    private fb: FormBuilder,
    private zone: NgZone,
    private router: Router,
    private alert: AlertService,
    private categoryService: CategoryService,
    private bookPrintService: BookPrintService,
    private service: AdminBookPublicationService,
    public cdr: ChangeDetectorRef,
    private userService: UserService,
    private dialog: MatDialog,
    private fileUpload: FileUploadService,
    private simulatorService: PublicSimulatorService,
  ) { }

  request: { listKitStruct: KitStruct[] } = {
    listKitStruct: []
  };

  ngOnInit(): void {
    this.model = this.fb.group({
      idUserAuthor: [null, [Validators.required, Validators.min(1)]],

      bookTitle: ['', [Validators.required]],
      coverAuthorName: ['', [Validators.required]],
      bookSubtitle: [''],
      versionName: ['', [Validators.required]],
      collectionName: [''],
      volumeName: [''],
      synopsis: ['', [Validators.required, Validators.minLength(500), Validators.maxLength(800)]],
      preface: ['', [Validators.maxLength(250)]],

      authorBio: ['', [Validators.required, Validators.maxLength(4000)]],
      idPhotoShape: [1, [Validators.required]],

      // arquivos -> apenas nomes
      authorPhotoFileName: [''],
      frontCoverFileName: [''],
      backCoverFileName: [''],
      coverForPrintFileName: [''],
      bookForPrintFileName: [''],
      bookForEbookFileName: [''],

      idPrimaryCategory: [null, [Validators.required]],
      idSecondaryCategory: [null, [Validators.required]],

      isbn: ['', [Validators.required, (c: AbstractControl) => this.isbnValidator(c)]],
      doi: [''],
      totalPages: [0, [Validators.required, Validators.min(1)]],

      idBookPrint: [null, [Validators.required, Validators.min(1)]],

      listAuthors: this.fb.array([this.fb.control('', Validators.required)]),
    });

    // kit inicial
    this.request.listKitStruct = [this.newKit()];
    this.applyAvailabilityRulesToAllKits();
    this.recalcAndClampAllKits();

    this.model.get('idPrimaryCategory')?.valueChanges.subscribe(() => this.refreshPrimaryAndSecondaryOptions());
    this.model.get('idSecondaryCategory')?.valueChanges.subscribe(() => this.refreshPrimaryAndSecondaryOptions());

    this.model.get('totalPages')!.valueChanges.subscribe((totalPages: number) => {
      if (!totalPages || totalPages <= 0) return;
      this.loadBookPrints(totalPages);
      this.applyAvailabilityRulesToAllKits();
      this.recalcAndClampAllKits();
    });
  }

  private setUploadLoading(key: keyof typeof this.uploadLoading, val: boolean) {
    this.uploadLoading[key] = val;
    this.cdr.markForCheck();
  }

  private hasPendingUploads(): boolean {
    return Object.values(this.uploadLoading).some(v => v === true);
  }

  isbnValidator(ctrl: AbstractControl): ValidationErrors | null {
    const raw = ((ctrl.value as string) || '').replace(/[^0-9Xx]/g, '');
    if (!raw) return { required: true };
    if (!(raw.length === 10 || raw.length === 13)) return { isbn: true };
    return null;
  }

  onLoginValidated(isLogged: boolean) {
    this.loggedIn = isLogged;
    if (!isLogged) return;

    this.loadCategories();
    this.loadUsers();
    this.loadPublicConfig();
  }

  private loadPublicConfig(): void {
    this.simulatorService.getPublicConfig().subscribe({
      next: (cfg) => {
        this.publicConfig = cfg;
        this.recalcAndClampAllKits();
        this.cdr.markForCheck();
      }
    });
  }

  private loadUsers(): void {
    this.userService.listActive(true).subscribe({
      next: (res: any) => {
        if (res?.isError) {
          this.alert.show('Erro', res.errorDescription || 'Falha ao carregar usuários.', AlertType.error);
          return;
        }

        this.allUsers = (res?.listUser || []).filter((u: UserSimpleStruct) => u.isActive);
        this.applyUserFilter(this.userSearch);
        this.cdr.markForCheck();
      },
      error: () => {
        this.alert.show('Erro', 'Erro inesperado ao carregar usuários.', AlertType.error);
      }
    });
  }

  onUserSearch(value: string) {
    this.userSearch = value;
    this.applyUserFilter(value);
  }

  private applyUserFilter(value: string) {
    const v = (value || '').toLowerCase();
    this.filteredUsers = this.allUsers
      .filter(u => !this.selectedUser || u.idUser !== this.selectedUser.idUser)
      .filter(u =>
        (u.userName?.toLowerCase().includes(v) || u.userEmail?.toLowerCase().includes(v))
      );

    this.cdr.markForCheck();
  }

  onUserSelected(u: UserSimpleStruct) {
    this.selectedUser = u;
    this.model.controls['idUserAuthor'].setValue(u.idUser);
    this.applyUserFilter(this.userSearch);
    this.cdr.markForCheck();
  }

  clearUser() {
    if (this.lockUserSelect) return;

    this.selectedUser = null;
    this.model.controls['idUserAuthor'].setValue(null);
    this.applyUserFilter(this.userSearch);
    this.cdr.markForCheck();
  }

  openCreateUserModal(): void {
    const ref = this.dialog.open(UserRegisterComponent, {
      data: { isAdminFlow: true }
    });

    ref.afterClosed().subscribe((created: boolean) => {
      if (created) this.loadUsers();
    });
  }

  // ========= FormArray: autores =========
  get authors(): FormArray {
    return this.model.get('listAuthors') as FormArray;
  }

  addAuthor(): void {
    this.authors.push(this.fb.control('', Validators.required));
    this.cdr.markForCheck();
  }

  removeAuthor(index: number): void {
    if (this.authors.length <= 1) return;
    this.authors.removeAt(index);
    this.cdr.markForCheck();
  }

  // ========= Categorias =========
  private buildAllOptionsSorted(): void {
    const byClass = new Map<number, { className: string; items: CategoryStruct[] }>();

    for (const c of this.listCategories) {
      if (!byClass.has(c.idCategoryClass)) {
        byClass.set(c.idCategoryClass, { className: c.categoryClassName, items: [] });
      }
      byClass.get(c.idCategoryClass)!.items.push(c);
    }

    const classes = [...byClass.entries()]
      .map(([classKey, v]) => ({
        classKey,
        className: v.className,
        items: v.items.sort((a, b) => a.categoryName.localeCompare(b.categoryName, 'pt-BR')),
      }))
      .sort((a, b) => a.className.localeCompare(b.className, 'pt-BR'));

    const result: CategoryOptionItem[] = [];
    for (const g of classes) {
      result.push({ isHeader: true, label: g.className, classKey: g.classKey });
      for (const it of g.items) {
        result.push({
          isHeader: false,
          label: it.categoryName,
          idCategory: it.idCategory,
          classKey: g.classKey
        });
      }
    }
    this.allOptions = result;
  }

  private buildFilteredOptions(excludeId: number | null): CategoryOptionItem[] {
    const out: CategoryOptionItem[] = [];
    let currentHeaderIndex: number | null = null;
    let currentHeaderHasItems = false;

    const flushEmptyHeader = () => {
      if (currentHeaderIndex != null && !currentHeaderHasItems) out.splice(currentHeaderIndex, 1);
      currentHeaderIndex = null;
      currentHeaderHasItems = false;
    };

    for (const opt of this.allOptions) {
      if (opt.isHeader) {
        flushEmptyHeader();
        out.push(opt);
        currentHeaderIndex = out.length - 1;
        currentHeaderHasItems = false;
      } else {
        if (excludeId && opt.idCategory === Number(excludeId)) continue;
        out.push(opt);
        currentHeaderHasItems = true;
      }
    }
    flushEmptyHeader();
    return out;
  }

  private refreshPrimaryAndSecondaryOptions(): void {
    const primary = this.model?.get('idPrimaryCategory')?.value ?? null;
    const secondary = this.model?.get('idSecondaryCategory')?.value ?? null;

    this.primaryOptions = this.buildFilteredOptions(secondary ? Number(secondary) : null);
    this.secondaryOptions = this.buildFilteredOptions(primary ? Number(primary) : null);
    this.cdr.markForCheck();
  }

  trackByOption = (_: number, item: CategoryOptionItem) =>
    item.isHeader ? `h-${item.classKey}-${item.label}` : `i-${item.idCategory}`;

  private loadCategories(): void {
    this.categoryService.getCategory().subscribe({
      next: (res) => {
        if (res?.isError) {
          this.alert.show('Erro', res.errorDescription || 'Falha ao carregar categorias.', AlertType.error);
          return;
        }

        this.listCategories = res.listCategoryStruct ?? [];
        this.buildAllOptionsSorted();
        this.refreshPrimaryAndSecondaryOptions();
        this.cdr.markForCheck();
      },
      error: () => this.alert.show('Erro', 'Erro ao carregar categorias.', AlertType.error)
    });
  }

  // ========= Prints =========
  loadBookPrints(totalPages: number): void {
    this.bookPrintService.getActiveByNumPages(totalPages).subscribe({
      next: (res: any) => {
        if (res?.isError) return;
        this.listBookPrint = res.listBookPrintStruct ?? res.listBookPrint ?? [];
        this.cdr.markForCheck();
      }
    });
  }

  selectBookPrint(id: number) {
    this.model.patchValue({ idBookPrint: id });
    this.recalcAndClampAllKits();
    this.cdr.markForCheck();
  }

  trackByPrint = (_: number, p: any) => p?.idBookPrint ?? _;

  // ========= Upload helpers =========
  onDragOver(ev: DragEvent) {
    ev.preventDefault();
  }

  private async fileToDataUrl(file: File): Promise<string> {
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error('Falha ao ler arquivo'));
      reader.readAsDataURL(file);
    });
  }

  private ensureImageLimit(file: File): boolean {
    if (file.size <= AdminBookPublicationComponent.MAX_IMAGE_BYTES) return true;
    this.alert.show('Imagem muito grande', 'Limite: 5 MB.', AlertType.warning);
    return false;
  }

  private ensurePdfLimit(file: File): boolean {
    if (file.size <= AdminBookPublicationComponent.MAX_PDF_BYTES) return true;
    this.alert.show(
      'PDF muito grande',
      `Arquivo com ${(file.size / (1024 * 1024)).toFixed(1)} MB. Limite: ~210 MB.`,
      AlertType.warning
    );
    return false;
  }

  /**
   * Fluxo completo:
   * 1) FileUpload/Create
   * 2) PUT no signed URL com headers retornados
   * 3) FileUpload/Finalize -> retorna fileName
   */
  private async uploadByPurpose(file: File, purpose: UploadPurposeEnum): Promise<FileUploadFinalizeResponse> {
    // 1) Create
    const createRes = await new Promise<FileUploadCreateResponse>((resolve, reject) => {
      this.fileUpload.create({
        originalFileName: file.name,
        contentType: file.type || 'application/octet-stream',
        sizeBytes: file.size,
        purpose: purpose
      }).subscribe({
        next: (x: any) => resolve(x as FileUploadCreateResponse),
        error: (e) => reject(e)
      });
    });

    if (!createRes?.idFileUploadSession || !createRes?.uploadUrl) {
      throw new Error('CreateUpload inválido (sem uploadId/url).');
    }

    // 2) PUT direto no signed URL com headers que o backend mandou
    const headers = new Headers();

    // headers extras vindos do backend (ex.: x-goog-*)
    const extraHeaders: Record<string, string> | null | undefined = (createRes as any).headers || createRes.headers || null;
    if (extraHeaders) {
      for (const k of Object.keys(extraHeaders)) {
        const v = extraHeaders[k];
        if (v != null) {
          headers.set(k, String(v));
        }
      }
    }

    // Se o backend não mandar Content-Type, seta o do arquivo
    if (!headers.has('Content-Type')) {
      headers.set('Content-Type', file.type || 'application/octet-stream');
    }

    const putResp = await fetch(createRes.uploadUrl, {
      method: 'PUT',
      headers,
      body: file
    });

    if (!putResp.ok) {
      const msg = await putResp.text().catch(() => '');
      throw new Error(`Falha no PUT (${putResp.status}). ${msg || ''}`.trim());
    }

    // 3) Finalize
    const fin = await new Promise<FileUploadFinalizeResponse>((resolve, reject) => {
      this.fileUpload
        .finalize({ idFileUploadSession: createRes.idFileUploadSession })
        .subscribe({
          next: (x) => resolve(x as FileUploadFinalizeResponse),
          error: (e) => reject(e)
        });
    });

    if (!fin?.fileName) {
      throw new Error('FinalizeUpload inválido (sem fileName).');
    }

    return fin;
  }

  // ===== Foto do autor =====
  onAuthorPhotoDrop(ev: DragEvent) {
    ev.preventDefault();
    ev.stopPropagation();
    const file = ev.dataTransfer?.files?.[0];
    if (!file) return;
    this.handleAuthorPhotoFile(file);
  }

  onAuthorPhotoSelected(ev: Event) {
    const input = ev.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    this.handleAuthorPhotoFile(file);
    input.value = '';
  }

  private async handleAuthorPhotoFile(file: File) {
    if (!this.ensureImageLimit(file)) return;

    this.setUploadLoading('authorPhoto', true);

    try {
      // preview local imediato
      const dataUrl = await this.fileToDataUrl(file);
      this.zone.run(() => {
        this.authorPhotoPreview = dataUrl;
        this.cdr.markForCheck();
      });

      const fin = await this.uploadByPurpose(file, UploadPurposeEnum.AuthorPhoto);

      this.zone.run(() => {
        this.model.patchValue({ authorPhotoFileName: fin.fileName });
        this.setUploadLoading('authorPhoto', false);
      });
    } catch (e: any) {
      this.zone.run(() => {
        this.alert.show('Erro', e?.message || 'Falha ao enviar a foto do autor.', AlertType.error);
        this.setUploadLoading('authorPhoto', false);
      });
    }
  }

  clearAuthorPhoto() {
    this.authorPhotoPreview = null;
    this.model.patchValue({ authorPhotoFileName: '' });
    this.cdr.markForCheck();
  }

  // ===== Capa/Contracapa =====
  onFrontCoverSelected(ev: Event) { this.handleImagePick(ev, 'front'); }
  onBackCoverSelected(ev: Event) { this.handleImagePick(ev, 'back'); }

  private async handleImagePick(ev: Event, which: 'front' | 'back') {
    const input = ev.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    if (!this.ensureImageLimit(file)) { input.value = ''; return; }

    const key = which === 'front' ? 'frontCover' : 'backCover';
    this.setUploadLoading(key as any, true);

    try {
      const dataUrl = await this.fileToDataUrl(file);

      this.zone.run(() => {
        if (which === 'front') this.frontCoverPreview = dataUrl;
        if (which === 'back') this.backCoverPreview = dataUrl;
        this.cdr.markForCheck();
      });

      const fin = await this.uploadByPurpose(file, which === 'front' ? UploadPurposeEnum.FrontCover : UploadPurposeEnum.BackCover);

      this.zone.run(() => {
        if (which === 'front') this.model.patchValue({ frontCoverFileName: fin.fileName });
        else this.model.patchValue({ backCoverFileName: fin.fileName });
        this.setUploadLoading(key as any, false);
      });
    } catch (e: any) {
      this.zone.run(() => {
        this.alert.show('Erro', e?.message || 'Falha ao enviar imagem.', AlertType.error);
        this.setUploadLoading(key as any, false);
      });
    } finally {
      input.value = '';
    }
  }

  clearFrontCover() {
    this.frontCoverPreview = null;
    this.model.patchValue({ frontCoverFileName: '' });
    this.cdr.markForCheck();
  }

  clearBackCover() {
    this.backCoverPreview = null;
    this.model.patchValue({ backCoverFileName: '' });
    this.cdr.markForCheck();
  }

  // ===== PDFs =====
  onCoverForPrintSelected(ev: Event) { this.handlePdfPick(ev, 'coverForPrintLabel', UploadPurposeEnum.CoverForPrintPdf); }
  onBookForPrintSelected(ev: Event) { this.handlePdfPick(ev, 'bookForPrintLabel', UploadPurposeEnum.BookForPrintPdf); }
  onBookForEbookSelected(ev: Event) { this.handlePdfPick(ev, 'bookForEbookLabel', UploadPurposeEnum.BookForEbookPdf); }

  private async handlePdfPick(
    ev: Event,
    labelField: 'coverForPrintLabel' | 'bookForPrintLabel' | 'bookForEbookLabel',
    purpose: UploadPurposeEnum
  ) {
    const input = ev.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    if (!this.ensurePdfLimit(file)) { input.value = ''; return; }

    const key =
      labelField === 'coverForPrintLabel' ? 'coverForPrint' :
        labelField === 'bookForPrintLabel' ? 'bookForPrint' :
          'bookForEbook';

    this.setUploadLoading(key as any, true);

    try {
      const fin = await this.uploadByPurpose(file, purpose);

      this.zone.run(() => {
        // salva fileName no form
        if (labelField === 'coverForPrintLabel') this.model.patchValue({ coverForPrintFileName: fin.fileName });
        if (labelField === 'bookForPrintLabel') this.model.patchValue({ bookForPrintFileName: fin.fileName });
        if (labelField === 'bookForEbookLabel') this.model.patchValue({ bookForEbookFileName: fin.fileName });

        // label visual
        (this as any)[labelField] = `${file.name} (${(file.size / (1024 * 1024)).toFixed(1)} MB)`;

        this.applyAvailabilityRulesToAllKits();
        this.recalcAndClampAllKits();

        this.setUploadLoading(key as any, false);
      });
    } catch (e: any) {
      this.zone.run(() => {
        this.alert.show('Erro', e?.message || 'Falha ao enviar PDF.', AlertType.error);
        this.setUploadLoading(key as any, false);
      });
    } finally {
      input.value = '';
    }
  }

  clearCoverForPrint() {
    this.coverForPrintLabel = null;
    this.model.patchValue({ coverForPrintFileName: '' });
    this.cdr.markForCheck();
    this.applyAvailabilityRulesToAllKits();
    this.recalcAndClampAllKits();
  }

  clearBookForPrint() {
    this.bookForPrintLabel = null;
    this.model.patchValue({ bookForPrintFileName: '' });
    this.cdr.markForCheck();
    this.applyAvailabilityRulesToAllKits();
    this.recalcAndClampAllKits();
  }

  clearBookForEbook() {
    this.bookForEbookLabel = null;
    this.model.patchValue({ bookForEbookFileName: '' });
    this.cdr.markForCheck();
    this.applyAvailabilityRulesToAllKits();
    this.recalcAndClampAllKits();
  }

  formatCurrency(value: number | null): string {
    if (value == null) return '';
    return value.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
    });
  }

  onCurrencyInput(event: Event, kit: any): void {
    const input = (event.target as HTMLInputElement);
    const raw = input.value.replace(/\D/g, '');
    const numeric = parseFloat(raw) / 100;
    kit.totalPrice = isNaN(numeric) ? 0 : Number(numeric.toFixed(2));
    input.value = this.formatCurrency(kit.totalPrice);
    this.cdr.markForCheck();
  }

  onCurrencyBlur(kit: any): void {
    if (!kit.totalPrice) kit.totalPrice = 0;
    this.applyPriceGuards(kit);
    this.cdr.markForCheck();
  }

  // ========= Kits =========
  private newKit(): KitStruct {
    return {
      idKit: 0,
      kitName: '',
      kitDescription: '',
      includesBook: false,
      includesEbook: false,
      includesVideoAccess: false,
      bookPriceInKit: 0,
      ebookPriceInKit: 0,
      videoAccessPriceInKit: 0,
      totalPrice: 10,
      isActive: true,
      selectedItems: []
    };
  }

  addKit(): void {
    this.request.listKitStruct.push(this.newKit());
    this.applyAvailabilityRulesToAllKits();
    this.recalcAndClampAllKits();
    this.cdr.markForCheck();
  }

  removeKit(i: number): void {
    if (this.request.listKitStruct.length <= 1) return;
    this.request.listKitStruct.splice(i, 1);
    this.applyAvailabilityRulesToAllKits();
    this.recalcAndClampAllKits();
    this.cdr.markForCheck();
  }

  get hasAnyCourse(): boolean {
    const chapters = this.listVideoChapter ?? [];
    if (!chapters.length) return false;

    for (const ch of chapters) {
      const list = (ch as any)?.listVideoStruct ?? (ch as any)?.listVideo ?? [];
      if (Array.isArray(list) && list.length) return true;
    }
    return false;
  }

  // Ebook disponível se já tem o FileName do ebook
  get hasEbookPdf(): boolean {
    const v = String(this.model?.value?.bookForEebookFileName || this.model?.value?.bookForEbookFileName || '');
    return v.length > 0;
  }

  // Livro físico disponível se anexou os PDFs de impressão
  get hasPrintPdfs(): boolean {
    const cover = String(this.model?.value?.coverForPrintFileName || '');
    const book = String(this.model?.value?.bookForPrintFileName || '');
    return cover.length > 0 && book.length > 0;
  }

  private applyAvailabilityRulesToKit(kit: any): void {
    if (!Array.isArray(kit.selectedItems)) kit.selectedItems = [];

    if (!this.hasPrintPdfs) kit.selectedItems = kit.selectedItems.filter((x: string) => x !== 'book');
    if (!this.hasEbookPdf) kit.selectedItems = kit.selectedItems.filter((x: string) => x !== 'ebook');
    if (!this.hasAnyCourse) kit.selectedItems = kit.selectedItems.filter((x: string) => x !== 'video');
  }

  private applyAvailabilityRulesToAllKits(): void {
    for (const kit of (this.request.listKitStruct ?? [])) {
      this.applyAvailabilityRulesToKit(kit);
    }
    this.cdr.markForCheck();
  }

  private getSelectedBookPrint(): any | null {
    const id = Number(this.model?.value?.idBookPrint || 0);
    if (!id) return null;
    return (this.listBookPrint || []).find(p => Number(p?.idBookPrint) === id) ?? null;
  }

  private getPhysicalUnitCostFromPrint(): number {
    const p = this.getSelectedBookPrint();
    const cost = Number(p?.estimatedTotalCost ?? 0);
    return isNaN(cost) ? 0 : cost;
  }

  includesPhysicalBook(kit: any): boolean {
    return Array.isArray(kit?.selectedItems) && kit.selectedItems.includes('book');
  }

  private asBookCostCalc(hasPhysical: boolean): BookCostCalc {
    return {
      unitCost: hasPhysical ? this.getPhysicalUnitCostFromPrint() : 0,
      platformProfitPercent: this.publicConfig?.platformProfitPercent ?? 0,
      baseTaxPercent: this.publicConfig?.baseTaxPercent ?? 0,
    };
  }

  getMinPriceForKit(kit: any): number {
    const hasPhysical = this.includesPhysicalBook(kit);
    const calc = this.asBookCostCalc(hasPhysical);
    return getMinPrice(calc, hasPhysical, 10);
  }

  applyPriceGuards(kit: any): void {
    this.applyAvailabilityRulesToKit(kit);

    const min = this.getMinPriceForKit(kit);
    const val = Number(kit?.totalPrice ?? 0);
    if (isNaN(val) || val < min) kit.totalPrice = min;

    this.cdr.markForCheck();
  }

  private recalcAndClampAllKits(): void {
    for (const kit of (this.request.listKitStruct ?? [])) {
      this.applyPriceGuards(kit);
    }
  }

  confirmPublish(): void {
    if (!this.loggedIn) {
      this.alert.show('Erro', 'Você não está autenticado.', AlertType.error);
      return;
    }

    if (this.hasPendingUploads()) {
      this.alert.show('Aguarde', 'Existem arquivos ainda sendo enviados. Aguarde terminar antes de publicar.', AlertType.warning);
      return;
    }

    if (this.model.invalid) {
      this.model.markAllAsTouched();
      this.alert.show('Erro', 'Existem campos obrigatórios inválidos.', AlertType.error);
      return;
    }

    const authorName = String(this.model.value.coverAuthorName || '').trim();
    const authorId = Number(this.model.value.idUserAuthor || 0);

    if (!authorId || authorId <= 0) {
      this.alert.show('Erro', 'Selecione um autor (usuário do sistema).', AlertType.error);
      return;
    }

    const msgAuthor = authorName ? `<b>${authorName}</b>` : `<b>ID ${authorId}</b>`;

    const ref = this.dialog.open(ConfirmModalComponent, {
      width: '520px',
      disableClose: true,
      data: {
        title: 'Confirmar publicação',
        description: `Você está publicando um livro em nome do autor ${msgAuthor}.`,
        innerHtml: `
        <div style="margin-top:10px;">
          <p style="margin:0 0 8px 0;">
            Esta ação <b>não pode ser revertida</b>.
          </p>
          <p style="margin:0;">
            Confirme para continuar.
          </p>
        </div>
      `,
        buttonConfirm: 'Publicar agora',
        buttonClose: 'Cancelar'
      }
    });

    ref.afterClosed().subscribe((result: any) => {
      if (result?.confirm === true) {
        this.publish();
      }
    });
  }

  // ========= Publish =========
  async publish(): Promise<void> {
    window.scrollTo({ top: 0, behavior: 'smooth' });

    if (!this.loggedIn) {
      this.alert.show('Erro', 'Você não está autenticado.', AlertType.error);
      return;
    }

    if (this.hasPendingUploads()) {
      this.alert.show('Aguarde', 'Existem arquivos ainda sendo enviados. Aguarde terminar antes de publicar.', AlertType.warning);
      return;
    }

    if (this.model.invalid) {
      this.model.markAllAsTouched();
      this.alert.show('Erro', 'Existem campos obrigatórios inválidos.', AlertType.error);
      return;
    }

    const idAuthor = Number(this.model.value.idUserAuthor || 0);
    if (idAuthor <= 0) {
      this.alert.show('Erro', 'Selecione um autor (usuário do sistema).', AlertType.error);
      return;
    }

    for (const kit of (this.request.listKitStruct ?? [])) {
      this.applyAvailabilityRulesToKit(kit);

      kit.includesBook = kit.selectedItems?.includes('book') || false;
      kit.includesEbook = kit.selectedItems?.includes('ebook') || false;
      kit.includesVideoAccess = kit.selectedItems?.includes('video') || false;
    }

    const listAuthors: string[] = (this.model.value.listAuthors || [])
      .map((x: any) => String(x || '').trim())
      .filter((x: string) => !!x);

    const req = new AdminBookPublicationRequest();
    req.idUserAuthor = Number(this.model.value.idUserAuthor);
    req.bookTitle = this.model.value.bookTitle;
    req.coverAuthorName = this.model.value.coverAuthorName;
    req.bookSubtitle = this.model.value.bookSubtitle;
    req.collectionName = this.model.value.collectionName;
    req.volumeName = this.model.value.volumeName;
    req.versionName = this.model.value.versionName;
    req.synopsis = this.model.value.synopsis;
    req.preface = this.model.value.preface;

    req.idPhotoShape = Number(this.model.value.idPhotoShape || 1);
    req.authorBio = this.model.value.authorBio;

    req.idPrimaryCategory = Number(this.model.value.idPrimaryCategory);
    req.idSecondaryCategory = Number(this.model.value.idSecondaryCategory);

    req.listAuthors = listAuthors;

    req.isbn = this.model.value.isbn;
    req.doi = this.model.value.doi;

    req.totalPages = Number(this.model.value.totalPages);
    req.idBookPrint = Number(this.model.value.idBookPrint);

    // nomes dos arquivos enviados
    req.authorPhotoFileName = this.model.value.authorPhotoFileName || null;
    req.frontCoverFileName = this.model.value.frontCoverFileName || null;
    req.backCoverFileName = this.model.value.backCoverFileName || null;
    req.coverForPrintFileName = this.model.value.coverForPrintFileName || null;
    req.bookForPrintFileName = this.model.value.bookForPrintFileName || null;
    req.bookForEbookFileName = this.model.value.bookForEbookFileName || null;

    req.listKitStruct = (this.request.listKitStruct || []).map(k => ({
      ...k,
      totalPrice: Number(k.totalPrice || 0),
      bookPriceInKit: Number(k.bookPriceInKit || 0),
      ebookPriceInKit: Number(k.ebookPriceInKit || 0),
      videoAccessPriceInKit: Number(k.videoAccessPriceInKit || 0),
      isActive: true
    }));

    req.isMarketplaceEnabled = this.isMarketplaceEnabled;

    this.isLoading = true;
    this.cdr.markForCheck();

    this.service.post(req).subscribe({
      next: (res: AdminBookPublicationResponse) => {
        if (res?.isError) {
          this.alert.show('Erro', res.errorDescription || 'Falha ao publicar.', AlertType.error);
          this.isLoading = false;
          this.cdr.markForCheck();
          return;
        }

        this.alert.show('Sucesso', `Livro publicado. #${res.idBookVersion}`, AlertType.success);

        this.router.navigateByUrl(`/revisor/${res.idBookVersion}`);
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.alert.show('Erro', 'Erro inesperado ao publicar.', AlertType.error);
        this.isLoading = false;
        this.cdr.markForCheck();
      }
    });
  }

  resetAll(): void {
    this.isMarketplaceEnabled = false;
    this.model.reset({
      idUserAuthor: null,
      bookTitle: '',
      coverAuthorName: '',
      bookSubtitle: '',
      versionName: '',
      collectionName: '',
      volumeName: '',
      synopsis: '',
      preface: '',
      authorBio: '',
      idPhotoShape: 1,

      authorPhotoFileName: '',
      frontCoverFileName: '',
      backCoverFileName: '',
      coverForPrintFileName: '',
      bookForPrintFileName: '',
      bookForEbookFileName: '',

      idPrimaryCategory: null,
      idSecondaryCategory: null,
      isbn: '',
      doi: '',
      totalPages: 0,
      idBookPrint: null,
      listAuthors: ['']
    });

    this.authorPhotoPreview = null;
    this.frontCoverPreview = null;
    this.backCoverPreview = null;

    this.coverForPrintLabel = null;
    this.bookForPrintLabel = null;
    this.bookForEbookLabel = null;

    this.request.listKitStruct = [this.newKit()];
    this.applyAvailabilityRulesToAllKits();
    this.recalcAndClampAllKits();

    this.cdr.markForCheck();
  }
}
